import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { adminAPI } from '../../api/client';
import { Card, Spinner, EmptyState, Avatar, fmtDate } from '../../components/UI';

const ACTION_META = {
  task_completed:        { icon: '✅', bg: '#dcfce7', label: 'Task Completed' },
  payment_sent:          { icon: '💰', bg: '#dcfce7', label: 'Payment Sent' },
  withdrawal_requested:  { icon: '📤', bg: '#fef3c7', label: 'Withdrawal Requested' },
  withdrawal_approved:   { icon: '🎉', bg: '#dcfce7', label: 'Withdrawal Approved' },
  withdrawal_rejected:   { icon: '❌', bg: '#fee2e2', label: 'Withdrawal Rejected' },
  earnings_added:        { icon: '💵', bg: '#eef1ff', label: 'Earnings Added' },
  account_created:       { icon: '👋', bg: '#eef1ff', label: 'Account Created' },
  login:                 { icon: '🔑', bg: '#f0f2f8', label: 'Login' },
};

export default function AdminActivity() {
  const [page, setPage] = useState(1);

  const fetchActivity = useCallback(() => adminAPI.activity(page), [page]);
  const { data, loading, error } = useFetch(fetchActivity);
  const activities = data?.activities || [];
  const pagination = data?.pagination  || {};

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h1 style={styles.h1}>Activity Log</h1>
        <p style={styles.sub}>Full audit trail of all user and admin actions across the platform.</p>
      </div>

      <Card>
        {loading ? <Spinner /> : error ? <p style={{ color: '#dc2626' }}>{error}</p>
          : activities.length === 0 ? <EmptyState icon="🕐" message="No activity recorded yet." />
          : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['User', 'Action', 'Description', 'Date'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map(a => {
                      const meta = ACTION_META[a.action_type] || { icon: '📌', bg: '#f0f2f8', label: a.action_type };
                      return (
                        <tr key={a.id} style={styles.tr}>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar name={a.user_name} size={28} />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f5e' }}>{a.user_name}</div>
                                <div style={{ fontSize: 11, color: '#9099b4' }}>{a.user_email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 30, height: 30, borderRadius: 8, background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                                {meta.icon}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#636b89', whiteSpace: 'nowrap' }}>{meta.label}</span>
                            </div>
                          </td>
                          <td style={styles.td}><span style={{ fontSize: 13, color: '#2e3456' }}>{a.description}</span></td>
                          <td style={styles.td}><span style={{ fontSize: 12, color: '#9099b4', whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pagination.total > pagination.limit && (
                <div style={styles.pagination}>
                  <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span style={{ fontSize: 13, color: '#636b89' }}>
                    Page {page} of {Math.ceil(pagination.total / pagination.limit)} · {pagination.total} total entries
                  </span>
                  <button style={styles.pageBtn} disabled={page * pagination.limit >= pagination.total} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )
        }
      </Card>
    </div>
  );
}

const styles = {
  wrap:       { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:     { marginBottom: 24 },
  h1:         { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:        { fontSize: 14, color: '#9099b4', marginTop: 4 },
  table:      { width: '100%', borderCollapse: 'collapse', minWidth: 600 },
  th:         { background: '#f8f9fc', padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #e2e6f0' },
  tr:         { borderBottom: '1px solid #f0f2f8' },
  td:         { padding: '13px 14px', fontSize: 14, verticalAlign: 'middle' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, paddingTop: 16 },
  pageBtn:    { padding: '7px 16px', border: '1.5px solid #e2e6f0', borderRadius: 8, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#636b89' },
};
